/**
 * Benefits — shared types + thin API client.
 *
 * Base path: /api/benefits (all authenticated, all org-scoped server side).
 * Every call goes through authFetch so the X-CBO-ID header is sent. Never use
 * raw fetch here.
 *
 * WHAT THESE SCREENS ARE FOR, AND WHAT THEY ARE NOT
 * This is staff-facing. The impact assessment says what the rules we hold say,
 * with the regulation behind each answer. It is not a determination by any
 * agency and it is not advice, and nothing here should be shown to a
 * participant without a human in between — which is why `requires_counsellor`
 * travels with every assessment and the UI reads it.
 */

import { authFetchJson } from '@/lib/authFetch';

const BASE = '/api/benefits';

// ── The intake instrument ────────────────────────────────────────
export type ResponseType = 'single' | 'multi' | 'scale';

export interface BenefitsOption {
  code: string;
  label: string;
  value: number;
  triggers_need: boolean;
}

export interface BenefitsItem {
  item_number: number;
  prompt: string;
  help_text: string | null;
  response_type: ResponseType;
  /**
   * EMPTY for items 3 and 4 (household size, monthly income) — those are free
   * numeric answers, not a pick-list. A radio group over zero options renders
   * nothing, which is how a question disappears from a form; the renderer has
   * to treat an empty option list as "ask for a number".
   */
  response_options: BenefitsOption[];
  domain_code: string | null;
  domain_name: string | null;
}

export interface BenefitsInstrument {
  instrument: {
    code: string;
    name: string;
    description: string;
    version: number;
    total_items: number;
  };
  items: BenefitsItem[];
}

// ── An administration of it ──────────────────────────────────────
export interface StartIntakeResult {
  administration_id: number;
  status: string;
  /** True when an intake was already open and we handed that one back. */
  resumed: boolean;
}

export interface DerivedIntake {
  benefits_received: string[];
  /**
   * On SNAP, Medicaid or TANF, so not income-tested for WIC at all. Derived,
   * never asked — she would have no way to answer it.
   */
  adjunctively_eligible: boolean;
  /** The one benefit with no available exemption. */
  has_ssi: boolean;
  /** Items answered "not sure". Carried, not resolved. */
  uncertain: string[];
  facts: Record<string, unknown>;
}

export interface ReadIntakeResult {
  administration_id: number;
  member_id: number;
  status: string;
  answers: Record<number, string | string[] | null>;
  derived: DerivedIntake;
}

export interface ItemAnswer {
  item_number: number;
  raw_response: string | string[] | null;
}

// ── What it means for her ────────────────────────────────────────
export type Treatment =
  | 'excluded'
  | 'counted'
  | 'counted_with_shield'
  | 'no_route'
  | 'unknown';

export interface BenefitImpact {
  program_code: string;
  program_name: string;
  treatment: Treatment;
  citation: string;
  explanation: string;
  what_changes_it: string | null;
  source_url: string | null;
  rule_version: number;
  period_type: string | null;
  period_cap: string | null;
  period_total: string | null;
  overshoot: string | null;
  is_harmful: boolean;
  needs_a_human: boolean;
}

export interface Assessment {
  state_code: string;
  assessed_on: string;
  any_harm: boolean;
  requires_counsellor: boolean;
  /** Benefits she receives that we hold no rule for. Named, never dropped. */
  unmodelled: string[];
  impacts: BenefitImpact[];
}

export interface PeriodEnvelopeState {
  program_id: number;
  period_type: string;
  period_cap: string;
  period_start: string;
  period_end: string;
  period_total: string;
  headroom: string;
  annual_envelope: string | null;
  enforcement: 'block' | 'warn';
  cap_basis: string;
}

export interface MemberImpactResult {
  member_id: number;
  state_code: string | null;
  /** Non-null when we will not answer, and why. Never a silent empty result. */
  blocked_reason: string | null;
  intake: DerivedIntake | null;
  envelope: PeriodEnvelopeState | null;
  assessment: Assessment | null;
}

// ── The rule catalogue ───────────────────────────────────────────
export interface BenefitRule {
  id: number;
  state_code: string;
  program_code: string;
  program_name: string;
  authority: string | null;
  treatment: Treatment;
  citation: string;
  explanation: string;
  what_changes_it: string | null;
  period_type: string | null;
  period_cap: string | null;
  over_cap_treatment: string | null;
  priority: number;
  rule_version: number;
  criteria: Record<string, unknown> | null;
  source_url: string | null;
}

// ── The payment envelope ─────────────────────────────────────────
export interface PaymentEnvelope {
  id: number;
  program_id: number;
  annual_envelope: string | null;
  period_type: 'federal_fiscal_quarter' | 'calendar_quarter' | 'month' | 'year';
  period_cap: string;
  cap_basis: string;
  benefit_rule_id: number | null;
  enforcement: 'block' | 'warn';
  is_active: boolean;
  effective_from: string;
  effective_to: string | null;
  updated_at: string | null;
  rule_citation: string | null;
  rule_program_code: string | null;
  periods_per_year: number;
  /** annual ÷ periods. The number that has to fit under period_cap. */
  even_split_per_period: string | null;
  /** How far an even schedule overshoots. Null when it fits. */
  even_split_overshoot: string | null;
  /** period_cap × periods — the annual envelope that would need no deferrals. */
  envelope_that_fits: string;
}

export interface EnvelopeResult {
  program_id: number;
  envelope: PaymentEnvelope | null;
  open_deferrals: number;
}

export interface EnvelopeInput {
  annual_envelope?: number | null;
  period_type: PaymentEnvelope['period_type'];
  period_cap: number;
  cap_basis: string;
  benefit_rule_id?: number | null;
  enforcement: 'block' | 'warn';
  effective_from?: string;
}

export interface DeferralRow {
  id: number;
  payout_id: number;
  member_id: number;
  member_name: string | null;
  period_type: string;
  period_start: string;
  period_end: string;
  period_cap: string;
  period_total_before: string;
  amount: string;
  would_have_been: string;
  deferred_to: string;
  reason: string;
  released_at: string | null;
  created_at: string;
  payout_status: string;
  cap_basis: string | null;
}

// ── Period labels ────────────────────────────────────────────────
export const PERIOD_LABELS: Record<PaymentEnvelope['period_type'], string> = {
  // Named in full because the distinction is the whole point: the federal
  // fiscal quarter starts 1 October, and treating it as a calendar quarter
  // moves every boundary by a month.
  federal_fiscal_quarter: 'Federal fiscal quarter (Oct–Dec, Jan–Mar, Apr–Jun, Jul–Sep)',
  calendar_quarter: 'Calendar quarter (Jan–Mar, Apr–Jun, Jul–Sep, Oct–Dec)',
  month: 'Calendar month',
  year: 'Calendar year',
};

export const TREATMENT_LABELS: Record<Treatment, string> = {
  excluded: 'Does not count',
  counted: 'Counts as income',
  counted_with_shield: 'Counts, with a rule that blunts it',
  no_route: 'Counts, and no exemption exists',
  unknown: 'We do not know',
};

// ── API client ───────────────────────────────────────────────────
export interface StateCodeResult {
  member_id: number;
  state_code: string | null;
  /** The free-text `members.state` column, shown for context — never used to set the code. */
  state_free_text: string | null;
  states_with_rules: string[];
}

export const benefitsApi = {
  getStateCode(memberId: number): Promise<StateCodeResult> {
    return authFetchJson(`${BASE}/members/${memberId}/state-code`);
  },

  setStateCode(memberId: number, stateCode: string | null): Promise<StateCodeResult> {
    return authFetchJson(`${BASE}/members/${memberId}/state-code`, {
      method: 'PUT',
      jsonBody: { state_code: stateCode },
    });
  },

  getInstrument(): Promise<BenefitsInstrument> {
    return authFetchJson(`${BASE}/instrument`);
  },

  startIntake(memberId: number): Promise<StartIntakeResult> {
    return authFetchJson(`${BASE}/intake`, {
      method: 'POST',
      jsonBody: { member_id: memberId },
    });
  },

  readIntake(administrationId: number): Promise<ReadIntakeResult> {
    return authFetchJson(`${BASE}/intake/${administrationId}`);
  },

  saveResponses(administrationId: number, responses: ItemAnswer[]): Promise<{ saved: number }> {
    return authFetchJson(`${BASE}/intake/${administrationId}/responses`, {
      method: 'PUT',
      jsonBody: { responses },
    });
  },

  submitIntake(administrationId: number): Promise<{ administration_id: number; status: string }> {
    return authFetchJson(`${BASE}/intake/${administrationId}/submit`, { method: 'POST' });
  },

  memberImpact(memberId: number, programId?: number | null): Promise<MemberImpactResult> {
    const q = programId ? `?program_id=${programId}` : '';
    return authFetchJson(`${BASE}/members/${memberId}/impact${q}`);
  },

  listRules(stateCode = 'MO'): Promise<{ state_code: string; rules: BenefitRule[] }> {
    return authFetchJson(`${BASE}/rules?state_code=${encodeURIComponent(stateCode)}`);
  },

  getEnvelope(programId: number): Promise<EnvelopeResult> {
    return authFetchJson(`${BASE}/programs/${programId}/envelope`);
  },

  saveEnvelope(programId: number, body: EnvelopeInput): Promise<EnvelopeResult> {
    return authFetchJson(`${BASE}/programs/${programId}/envelope`, {
      method: 'PUT',
      jsonBody: body,
    });
  },

  listDeferrals(programId: number, openOnly = true): Promise<{ deferrals: DeferralRow[] }> {
    return authFetchJson(`${BASE}/programs/${programId}/deferrals?open_only=${openOnly}`);
  },
};
